import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { API, RELATIONSHIPS, toISODate, errDetail } from './adminShared';

const EMPTY_FORM = { serial_number: '', family_id: '', first_name: '', middle_name: '', last_name: '', dob: '', sex: 'Male', relationship: 'Principle' };

export default function MemberFormDialog({ open, onOpenChange, member, families, authCfg, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const isEdit = !!member;

  useEffect(() => {
    if (open) {
      setForm(member ? {
        serial_number: member.serial_number,
        family_id: member.family_id || '',
        first_name: member.first_name,
        middle_name: member.middle_name || '',
        last_name: member.last_name,
        dob: toISODate(member.dob),
        sex: member.sex || 'Male',
        relationship: member.relationship || 'Principle',
      } : EMPTY_FORM);
    }
  }, [open, member]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/members/${member.serial_number}`, {
          first_name: form.first_name,
          middle_name: form.middle_name,
          last_name: form.last_name,
          dob: toISODate(form.dob),
          sex: form.sex,
          relationship: form.relationship,
        }, authCfg);
        toast.success('Member updated successfully');
      } else {
        await axios.post(`${API}/admin/members`, { ...form, dob: toISODate(form.dob) }, authCfg);
        toast.success('Member created successfully');
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(errDetail(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Member' : 'Add Member'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Serial Number</Label>
            <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} required disabled={isEdit} data-testid="member-form-serial" />
          </div>
          <div>
            <Label>Family ID</Label>
            <Select value={form.family_id || 'none'} onValueChange={(value) => setForm({ ...form, family_id: value === 'none' ? '' : value })} disabled={isEdit}>
              <SelectTrigger data-testid="member-form-family"><SelectValue placeholder="Select family" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select a family</SelectItem>
                {families.map((f) => (<SelectItem key={f.family_id} value={f.family_id}>{f.family_id} - {f.principle_member_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>First Name</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="member-form-first-name" />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="member-form-last-name" />
            </div>
          </div>
          <div>
            <Label>Middle Name</Label>
            <Input value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date of Birth</Label>
              <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} required />
            </div>
            <div>
              <Label>Sex</Label>
              <Select value={form.sex || 'Male'} onValueChange={(value) => setForm({ ...form, sex: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Relationship</Label>
            <Select value={form.relationship || 'Principle'} onValueChange={(value) => setForm({ ...form, relationship: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {RELATIONSHIPS.map((r) => (<SelectItem key={r} value={r}>{r === 'Principle' ? 'Principal' : r}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={loading} className="w-full" data-testid="member-form-submit">
            {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
